/**
 * PB-FND-60 — Shared bundle row encode (sync autofill + manual row save).
 * One path for aiImageFromSyncedProps + encodeFlowStateForCart on bundle rows.
 */
import { collectAiImageDraftFromDom, collectAiTextDraftFromDom, buildAiTextDraftFromCartProps, mergeAiImageFieldIntoSelections } from '@theme/pb-dl-ai-hydrate';
import { isValidAiMediaTier1Value, purgeInvalidAiMediaTier1Keys } from '@theme/pb-properties';
import { assertEncodeBeltReady, ENCODE_BELT_NOT_READY_MESSAGE, resolveMainAiTextByLabel } from '@theme/pb-flush-encode';
import { collectAiTextAllowedKeys, encodeAiTextFieldProps } from '@theme/pb-ai-text-logic';

/** Sanitize raw anchor/slot overlays before their properties can fan out to other rows. */
export function applyMainAiTextProps(props, item, state) {
  const fields = (item?.config || []).filter((field) => String(field?.type || '').toLowerCase().includes('ai text'));
  if (!fields.length) return props;
  const result = { ...props };
  const draft = resolveMainAiTextByLabel('#main-flow-container', {
    builderState: { ...state, mode: 'MAIN' },
  });
  for (const field of fields) {
    delete result[field.label];
    for (const key of collectAiTextAllowedKeys([field])) delete result[key];
    Object.assign(result, encodeAiTextFieldProps(field, draft?.byLabel?.[field.label], props));
  }
  return result;
}

function getSyncApi(pb) {
  if (pb && typeof pb._offerBundleSyncApi === 'function') {
    const api = pb._offerBundleSyncApi();
    if (api) return api;
  }
  if (typeof window !== 'undefined' && window.PBOfferBundleSync) return window.PBOfferBundleSync;
  return null;
}

function buildAiImageAdapters(pb) {
  return {
    getBundlePropRawByNormalizedKey: pb.getBundlePropRawByNormalizedKey
      ? pb.getBundlePropRawByNormalizedKey.bind(pb)
      : null,
    isBundlePropertyValueEmpty: pb.isBundlePropertyValueEmpty
      ? pb.isBundlePropertyValueEmpty.bind(pb)
      : null,
    getAiImagePhotoKeyFromField: pb.getAiImagePhotoKeyFromField
      ? pb.getAiImagePhotoKeyFromField.bind(pb)
      : null,
    getAiImageCharacterKeyFromField: pb.getAiImageCharacterKeyFromField
      ? pb.getAiImageCharacterKeyFromField.bind(pb)
      : null,
    isAiImageField(field) {
      return !!(field && field.type && String(field.type).toLowerCase().indexOf('ai image') !== -1);
    },
  };
}

/**
 * True when main-flow (not bundle row SUB) owns a Design Library pick.
 * Prevents row-scoped DL state from merging onto anchor / main cart lines.
 */
export function mainOwnsDesignLibraryPick(state) {
  if (!state || !state.main) return false;
  const touched = state.main.touched || {};
  if (touched['Design Library'] || touched.Design) return true;
  const byLabel = state.catalogDesignLibrary && state.catalogDesignLibrary.byLabel;
  if (byLabel) {
    for (const label of Object.keys(byLabel)) {
      if (touched[label]) return true;
    }
  }
  const sel = state.main.selections || {};
  if (byLabel) {
    for (const label of Object.keys(byLabel)) {
      const val = sel[label];
      if (val != null && String(val).trim() !== '') return true;
    }
  }
  const file = sel['Design File'];
  const source = sel['Design Source'];
  if (file != null && file !== '') return true;
  if (source != null && source !== '') return true;
  return Object.keys(sel).some(function (key) {
    const nested = sel[key];
    if (!nested || typeof nested !== 'object') return false;
    const nf = nested['Design File'] || nested.previewUrl || nested.productionUrl;
    const ns = nested['Design Source'] || nested.selectionMode;
    return (nf != null && nf !== '') || (ns != null && ns !== '');
  });
}

/**
 * Row-scoped catalogDesignLibrary — props baseline, then saved row state.
 * Never reads root builderState.catalogDesignLibrary (main-flow canonical).
 *
 * @param {object} sourceProps expanded Tier-1 props
 * @param {object} targetItem bundle row item
 * @param {object} pb productBuilder
 * @param {object} [options]
 */
export function resolveRowCatalogDesignLibrary(sourceProps, targetItem, pb, options = {}) {
  let catalog = null;
  if (typeof pb.catalogDesignLibraryFromSyncedProps === 'function') {
    catalog = pb.catalogDesignLibraryFromSyncedProps(sourceProps, targetItem);
  }
  if (options.rowCatalogDesignLibrary?.byLabel) {
    const rowByLabel = options.rowCatalogDesignLibrary.byLabel;
    if (!catalog) catalog = { byLabel: {} };
    if (!catalog.byLabel) catalog.byLabel = {};
    Object.keys(rowByLabel).forEach(function (label) {
      catalog.byLabel[label] = rowByLabel[label];
    });
  }
  return catalog && catalog.byLabel && Object.keys(catalog.byLabel).length ? catalog : undefined;
}

/**
 * Row-scoped aiImageByLabel — props baseline, then saved row state, then live DOM.
 * Never reads root builderState.aiImageByLabel (main-flow canonical).
 *
 * @param {object} sourceProps expanded Tier-1 props
 * @param {object} targetItem bundle row item
 * @param {object} pb productBuilder
 * @param {object} [options]
 */
export function resolveRowAiImageByLabel(sourceProps, targetItem, pb, options = {}) {
  const api = options.syncApi || getSyncApi(pb);
  const adapters = options.adapters || buildAiImageAdapters(pb);
  const byLabel = {};

  if (options.rowAiImageByLabel && options.rowAiImageByLabel.byLabel) {
    Object.assign(byLabel, options.rowAiImageByLabel.byLabel);
  }

  if (api && typeof api.aiImageFromSyncedProps === 'function') {
    const fromProps = api.aiImageFromSyncedProps(sourceProps, targetItem, adapters);
    if (fromProps && fromProps.byLabel) Object.assign(byLabel, fromProps.byLabel);
  }

  if (options.collectFromDom) {
    const selector = options.containerSelector || '#sub-flow-container';
    const fromDom = options.collectFromDom === true
      ? collectAiImageDraftFromDom(selector)
      : typeof options.collectFromDom === 'function'
        ? options.collectFromDom(selector)
        : {};
    Object.assign(byLabel, fromDom);
  }

  return Object.keys(byLabel).length ? { byLabel } : undefined;
}

/**
 * Belt-and-suspenders Tier-1 keys after encode (mirrors buildSyncedBundleRowProperties).
 */
export function reinforceBundleRowTier1Keys(encoded, sourceProps, targetItem, pb, aiImageByLabel) {
  if (!encoded || typeof encoded !== 'object') return encoded;
  const empty =
    typeof pb.isBundlePropertyValueEmpty === 'function'
      ? pb.isBundlePropertyValueEmpty.bind(pb)
      : function (v) {
          return v == null || v === '';
        };

  const catalogDesignLibrary =
    typeof pb.catalogDesignLibraryFromSyncedProps === 'function'
      ? pb.catalogDesignLibraryFromSyncedProps(sourceProps, targetItem)
      : null;
  if (catalogDesignLibrary) {
    let tier1 = {};
    if (typeof pb.encodeDesignLibraryPropsFromState === 'function') {
      tier1 = pb.encodeDesignLibraryPropsFromState(
        { catalogDesignLibrary: catalogDesignLibrary, selections: sourceProps },
        targetItem.config || []
      );
    } else if (typeof pb.catalogDesignLibraryToTier1Props === 'function') {
      tier1 = pb.catalogDesignLibraryToTier1Props(catalogDesignLibrary, sourceProps);
    }
    Object.keys(tier1 || {}).forEach(function (key) {
      if (!empty(tier1[key]) && empty(encoded[key])) encoded[key] = tier1[key];
    });
  }

  if (aiImageByLabel) {
    (targetItem.config || []).forEach(function (field) {
      if (!field || !field.type || String(field.type).toLowerCase().indexOf('ai image') === -1) return;
      const pk = typeof pb.getAiImagePhotoKeyFromField === 'function' ? pb.getAiImagePhotoKeyFromField(field) : null;
      const ck =
        typeof pb.getAiImageCharacterKeyFromField === 'function' ? pb.getAiImageCharacterKeyFromField(field) : null;
      const photo =
        pk && typeof pb.getBundlePropRawByNormalizedKey === 'function'
          ? pb.getBundlePropRawByNormalizedKey(sourceProps, pk)
          : pk
            ? sourceProps[pk]
            : undefined;
      const char =
        ck && typeof pb.getBundlePropRawByNormalizedKey === 'function'
          ? pb.getBundlePropRawByNormalizedKey(sourceProps, ck)
          : ck
            ? sourceProps[ck]
            : undefined;
      if (pk && !empty(photo) && empty(encoded[pk]) && isValidAiMediaTier1Value(pk, photo)) encoded[pk] = photo;
      if (ck && !empty(char) && empty(encoded[ck]) && isValidAiMediaTier1Value(ck, char)) encoded[ck] = char;
    });
  }

  return purgeInvalidAiMediaTier1Keys(encoded);
}

/**
 * @param {object} targetItem
 * @param {object} expandedP13n flat Tier-1 props
 * @param {object} pb
 * @param {object} [options]
 */
export function buildBundleRowEncodeStateSlice(targetItem, expandedP13n, pb, options = {}) {
  const sourceProps = expandedP13n && typeof expandedP13n === 'object' ? { ...expandedP13n } : {};
  const nested =
    typeof pb.nestBundleSelectionsForAiImageFields === 'function'
      ? pb.nestBundleSelectionsForAiImageFields(targetItem, sourceProps)
      : sourceProps;
  const catalogDesignLibrary = resolveRowCatalogDesignLibrary(sourceProps, targetItem, pb, options);
  const aiImageByLabel = resolveRowAiImageByLabel(sourceProps, targetItem, pb, options);

  return {
    selections: nested,
    topicSelections: options.topicSelections || {},
    catalogDesignLibrary,
    aiImageByLabel,
    aiTextByLabel: options.rowAiTextByLabel || (options.trustedAiTextProps
      ? buildAiTextDraftFromCartProps(sourceProps, targetItem.config || [], sourceProps)
      : undefined),
  };
}

/**
 * Shared encode for sync autofill and manual bundle row save.
 */
export function encodeBundleRowProperties(targetItem, expandedP13n, pb, options = {}) {
  const sourceProps = purgeInvalidAiMediaTier1Keys(
    expandedP13n && typeof expandedP13n === 'object' ? { ...expandedP13n } : {}
  );
  const stateSlice = buildBundleRowEncodeStateSlice(targetItem, sourceProps, pb, options);

  assertEncodeBeltReady(pb, 'encodeBundleRowProperties');
  if (typeof pb.encodeFlowStateForCart !== 'function') {
    const err = new Error(ENCODE_BELT_NOT_READY_MESSAGE);
    err.code = 'PB_ENCODE_BELT_NOT_READY';
    err.context = 'encodeBundleRowProperties:encodeFlowStateForCart';
    throw err;
  }

  const encoded = pb.encodeFlowStateForCart(stateSlice, targetItem, targetItem.options || []);
  let result = reinforceBundleRowTier1Keys(encoded, sourceProps, targetItem, pb, stateSlice.aiImageByLabel);
  if (typeof pb.upgradeLegacyCartLinePropsOnSave === 'function') {
    result = pb.upgradeLegacyCartLinePropsOnSave(result, targetItem.config || [], {
      sourceProps: options.sourceProps || sourceProps,
    });
  }
  return result;
}

/** Flat Tier-1 props from live SUB flow selections (post-flush). */
export function extractExpandedPropsFromSubFlow(pb, item, sub) {
  const selections = (sub && sub.selections) || {};
  let flat =
    typeof pb.flattenAiImageSelectionsForProps === 'function'
      ? pb.flattenAiImageSelectionsForProps(item, selections)
      : { ...selections };
  if (typeof pb.expandDelimiterProperties === 'function') {
    flat = pb.expandDelimiterProperties({ ...flat });
  }
  return flat;
}

/**
 * Before configureBundleItem opens SUB — rebuild row-scoped media from slot props.
 * Clears root aiImageByLabel so main-flow AI does not leak into bundle row edits.
 * Row-scopes catalogDesignLibrary on sub — root catalog stays main-owned only.
 */
export function prepareBundleRowSubContext(state, item, slotProps, pb) {
  if (!state || !state.sub) return;
  if (state.aiImageByLabel) delete state.aiImageByLabel;

  let expanded = null;
  if (slotProps && typeof slotProps === 'object' && Object.keys(slotProps).length) {
    expanded =
      typeof pb.expandDelimiterProperties === 'function'
        ? pb.expandDelimiterProperties({ ...slotProps })
        : { ...slotProps };
  }

  const encodeOpts = { syncApi: getSyncApi(pb) };
  if (expanded) {
    const aiImageByLabel = resolveRowAiImageByLabel(expanded, item, pb, encodeOpts);
    if (aiImageByLabel) state.sub.aiImageByLabel = aiImageByLabel;
    else delete state.sub.aiImageByLabel;

    const catalogDesignLibrary = resolveRowCatalogDesignLibrary(expanded, item, pb, encodeOpts);
    if (catalogDesignLibrary) state.sub.catalogDesignLibrary = catalogDesignLibrary;
    else delete state.sub.catalogDesignLibrary;
  } else {
    delete state.sub.aiImageByLabel;
    delete state.sub.catalogDesignLibrary;
  }
}

/** Draft serialize — prefer row-scoped sub.aiImageByLabel during bundle queue SUB. */
export function resolveRowAiImageForDraft(state) {
  if (state && state.mode === 'SUB' && state.sub && state.sub.isQueueItem && state.sub.aiImageByLabel) {
    return state.sub.aiImageByLabel.byLabel || null;
  }
  return state && state.aiImageByLabel ? state.aiImageByLabel.byLabel || null : null;
}

/** Draft serialize — prefer row-scoped sub.catalogDesignLibrary during bundle queue SUB. */
export function resolveRowCatalogDesignLibraryForDraft(state) {
  if (state && state.mode === 'SUB' && state.sub && state.sub.isQueueItem && state.sub.catalogDesignLibrary) {
    return state.sub.catalogDesignLibrary;
  }
  return state && state.catalogDesignLibrary ? state.catalogDesignLibrary : null;
}

/**
 * Cart encode — Design File only on the bundle row that owns the pick.
 * Anchor may use main catalog only when main owns the DL pick.
 */
export function resolveCatalogDesignLibraryForCartRow(state, idx, firstMainIdx, effProps, item, pb) {
  const isAnchor = idx === firstMainIdx;
  if (isAnchor) {
    if (!mainOwnsDesignLibraryPick(state)) return null;
    return state.catalogDesignLibrary || null;
  }
  return resolveRowCatalogDesignLibrary(effProps || {}, item, pb || {}) || null;
}

/**
 * Persist SUB/row AI draft after flush — mirrors main persist before autofill encode.
 * Merges saved row draft → flushed selections → live DOM (later wins).
 * @param {object} state builderState
 * @param {{ item?: object, pb?: object, collectFromDom?: Function }} [options]
 */
export function persistRowAiImageDraftToState(state, options = {}) {
  if (!state || state.mode !== 'SUB' || !state.sub?.isQueueItem) return;

  const sub = state.sub;
  const byLabel = {};
  const collect =
    options.collectFromDom === undefined
      ? collectAiImageDraftFromDom
      : typeof options.collectFromDom === 'function'
        ? options.collectFromDom
        : () => ({});

  if (sub.aiImageByLabel?.byLabel) {
    Object.assign(byLabel, sub.aiImageByLabel.byLabel);
  }

  const item = options.item;
  const pb = options.pb;
  if (item && pb) {
    const expanded = extractExpandedPropsFromSubFlow(pb, item, sub);
    const api = options.syncApi || getSyncApi(pb);
    const adapters = options.adapters || buildAiImageAdapters(pb);
    if (api && typeof api.aiImageFromSyncedProps === 'function') {
      const fromSelections = api.aiImageFromSyncedProps(expanded, item, adapters);
      if (fromSelections?.byLabel) Object.assign(byLabel, fromSelections.byLabel);
    }
  }

  const fromDom = collect('#sub-flow-container') || {};
  Object.assign(byLabel, fromDom);

  if (Object.keys(byLabel).length) {
    sub.aiImageByLabel = { byLabel };
  } else {
    delete sub.aiImageByLabel;
  }
}

/**
 * Write normalized AI tier-1 keys back to sub.selections before row encode.
 * @param {object} item bundle row item
 * @param {object} state builderState
 * @param {object} pb productBuilder
 */
export function normalizeRowAiImageSelectionsForEncode(item, state, pb) {
  if (!item || !state?.sub?.selections || !pb) return;
  const sub = state.sub;
  let byLabel = null;
  if (sub.aiImageByLabel?.byLabel) {
    byLabel = sub.aiImageByLabel.byLabel;
  } else if (typeof pb.resolveMainAiImageTier1PropsForSync === 'function') {
    const tier1 = pb.resolveMainAiImageTier1PropsForSync(item, state);
    if (tier1 && Object.keys(tier1).length) {
      Object.keys(tier1).forEach((key) => {
        if (typeof pb.isBundlePropertyValueEmpty === 'function' && pb.isBundlePropertyValueEmpty(tier1[key])) {
          return;
        }
        sub.selections[key] = tier1[key];
      });
    }
  }
  if (!byLabel) return;

  const aiFieldLabels = new Set();
  const nestedMediaKeys = new Set();
  (item.config || []).forEach((field) => {
    if (!field?.type || String(field.type).toLowerCase().indexOf('ai image') === -1 || !field.label) return;
    const saved = byLabel[field.label];
    if (!saved || typeof saved !== 'object') return;
    const pk = typeof pb.getAiImagePhotoKeyFromField === 'function' ? pb.getAiImagePhotoKeyFromField(field) : null;
    const ck = typeof pb.getAiImageCharacterKeyFromField === 'function' ? pb.getAiImageCharacterKeyFromField(field) : null;
    const photoUrl = saved.photoUrl && String(saved.photoUrl).trim();
    const charUrls = (saved.characterUrls || []).filter((u) => u != null && String(u).trim() !== '');
    const pickIdx = Math.max(0, Math.min(Number(saved.selectedIndex) || 0, Math.max(charUrls.length - 1, 0)));
    const charUrl = charUrls.length ? String(charUrls[pickIdx]).trim() : '';
    const nested = {};
    if (pk && photoUrl && isValidAiMediaTier1Value(pk, photoUrl)) nested[pk] = photoUrl;
    if (ck && charUrl && isValidAiMediaTier1Value(ck, charUrl)) nested[ck] = charUrl;
    if (Object.keys(nested).length) sub.selections[field.label] = nested;
    if (pk) {
      nestedMediaKeys.add(pk);
      delete sub.selections[pk];
    }
    if (ck) {
      nestedMediaKeys.add(ck);
      if (ck !== field.label) delete sub.selections[ck];
    }
    aiFieldLabels.add(field.label);
  });
}

/**
 * Cart bundle finalize — same row encode belt as sync/autofill (PB-FND-78).
 * Uses encodeBundleRowProperties (aiImageFromSyncedProps + reinforceBundleRowTier1Keys),
 * not selections-only encodeCartLineProperties that drops row-scoped Character.
 *
 * @param {object} params
 * @param {object} params.pb productBuilder
 * @param {object} params.item bundle row item
 * @param {object} params.effProps from getEffectiveBundleRowProperties
 * @param {number} params.idx row index
 * @param {number} params.firstMainIdx anchor index
 * @param {object} params.state builderState
 * @param {object} [params.extraProps] _Bundle, Add-ons, etc.
 * @param {object} [params.topicSelections]
 * @param {object} [params.catalogDesignLibrary]
 */
export function encodeBundleCartRowProperties(params) {
  const pb = params.pb;
  const item = params.item;
  const effProps = params.effProps || {};
  const idx = params.idx;
  const firstMainIdx = params.firstMainIdx;
  const state = params.state;
  const extraProps = params.extraProps || {};

  let expanded =
    typeof pb.expandDelimiterProperties === 'function'
      ? pb.expandDelimiterProperties({ ...effProps })
      : { ...effProps };

  let topicSelections =
    params.topicSelections != null
      ? { ...params.topicSelections }
      : idx === firstMainIdx
        ? { ...(state?.main?.topicSelections || {}) }
        : {};

  let catalogDesignLibrary =
    params.catalogDesignLibrary !== undefined ? params.catalogDesignLibrary : null;
  if (catalogDesignLibrary === null) {
    catalogDesignLibrary = resolveCatalogDesignLibraryForCartRow(
      state,
      idx,
      firstMainIdx,
      effProps,
      item,
      pb
    );
  }

  if (typeof pb.decodeCartLineToBuilderState === 'function') {
    const fields =
      typeof pb.getPersonalizationFields === 'function' ? pb.getPersonalizationFields(item) : [];
    const decoded = pb.decodeCartLineToBuilderState(
      { properties: expanded },
      fields,
      { options: item.options || state?.main?.options || [] }
    );
    if (decoded) {
      if (decoded.selections && typeof decoded.selections === 'object') {
        Object.keys(decoded.selections).forEach(function (k) {
          if (expanded[k] == null || expanded[k] === '') {
            expanded[k] = decoded.selections[k];
          }
        });
      }
      if (decoded.catalogDesignLibrary) {
        if (idx === firstMainIdx) {
          catalogDesignLibrary = decoded.catalogDesignLibrary;
        } else if (!catalogDesignLibrary) {
          catalogDesignLibrary = decoded.catalogDesignLibrary;
        }
      }
      if (decoded.topicSelections && Object.keys(decoded.topicSelections).length) {
        topicSelections = { ...decoded.topicSelections };
      }
    }
  }

  const encodeOpts = {
    topicSelections,
    syncApi: getSyncApi(pb),
  };
  if (catalogDesignLibrary) {
    encodeOpts.rowCatalogDesignLibrary = catalogDesignLibrary;
  }
  const isAnchor = idx === firstMainIdx;
  encodeOpts.rowAiTextByLabel = buildAiTextDraftFromCartProps(effProps, item.config || [], expanded);
  if (state?.mode === 'SUB' && state.sub?.isQueueItem && String(state.sub.currentId) === String(idx)) {
    encodeOpts.rowAiTextByLabel = state.sub.aiTextByLabel || { byLabel: {} };
  } else if (isAnchor) {
    encodeOpts.rowAiTextByLabel = state?.aiTextByLabel || { byLabel: {} };
  }
  if (
    state?.mode === 'SUB' &&
    state.sub?.isQueueItem &&
    String(state.sub.currentId) === String(idx) &&
    state.sub.aiImageByLabel
  ) {
    encodeOpts.rowAiImageByLabel = state.sub.aiImageByLabel;
  } else if (isAnchor && state?.aiImageByLabel) {
    encodeOpts.rowAiImageByLabel = state.aiImageByLabel;
  } else {
    const fromSlot = resolveRowAiImageByLabel(expanded, item, pb, {
      syncApi: encodeOpts.syncApi,
    });
    if (fromSlot) encodeOpts.rowAiImageByLabel = fromSlot;
  }

  let props = encodeBundleRowProperties(item, expanded, pb, encodeOpts);
  if (extraProps && typeof extraProps === 'object') {
    props = { ...props, ...extraProps };
  }

  return {
    properties: props,
    topicSelections,
    catalogDesignLibrary,
    expanded,
  };
}

/**
 * Manual bundle row save after flush — same encode contract as sync autofill.
 * @param {{ pb: object, item: object, state: object, collectFromDom?: Function }} params
 */
export function encodeManualBundleRowSave(params) {
  const pb = params.pb;
  const item = params.item;
  const state = params.state;
  const sub = state && state.sub;
  if (!pb || !item || !sub) return {};

  persistRowAiImageDraftToState(state, { collectFromDom: params.collectFromDom, item, pb });
  normalizeRowAiImageSelectionsForEncode(item, state, pb);

  const expanded = extractExpandedPropsFromSubFlow(pb, item, sub);
  const collectText = params.collectAiTextFromDom || collectAiTextDraftFromDom;
  const rowAiTextByLabel = { byLabel: {
    ...sub.aiTextByLabel?.byLabel,
    ...collectText('#sub-flow-container'),
  } };
  return encodeBundleRowProperties(item, expanded, pb, {
    collectFromDom: params.collectFromDom ?? true,
    containerSelector: '#sub-flow-container',
    rowAiImageByLabel: sub.aiImageByLabel,
    rowAiTextByLabel,
    rowCatalogDesignLibrary: sub.catalogDesignLibrary,
    topicSelections: sub.topicSelections,
    sourceProps: params.sourceProps,
  });
}

/**
 * PB-AI-9 — Write terminal AI Photo/Character into bundle row slot properties.
 * Offer thumbs + cart encode read configurations[bid][idx].properties — not live DOM.
 *
 * @returns {boolean} true when slot properties were updated
 */
export function projectAiMediaToBundleSlot(state, pb, bundleId, rowIdx, options = {}) {
  if (!state?.bundle || !pb || bundleId == null || rowIdx == null) return false;
  const idx = Number(rowIdx);
  if (Number.isNaN(idx)) return false;

  const bid = String(bundleId);
  const data = typeof window !== 'undefined' ? window.BUILDER_DATA : null;
  const bundle =
    options.bundle || data?.bundles?.find((b) => String(b?.id) === bid);
  if (!bundle?.items?.[idx]) return false;

  const item = bundle.items[idx];
  const mainProduct = options.mainProduct || data?.mainProduct;
  const sameId = (a, b) => String(a?.id) === String(b?.id);
  const firstMainIdx = bundle.items.findIndex((i) => sameId(i, mainProduct));
  const isAnchor = idx === firstMainIdx && sameId(item, mainProduct);

  let rowAiImageByLabel = options.rowAiImageByLabel;
  if (!rowAiImageByLabel?.byLabel) {
    if (state.mode === 'SUB' && state.sub?.isQueueItem && String(state.sub.currentId) === String(idx)) {
      rowAiImageByLabel = state.sub.aiImageByLabel;
    } else if (isAnchor) {
      const flush = typeof window !== 'undefined' ? window.PBFlushEncode : null;
      rowAiImageByLabel =
        options.mainAiImageByLabel ||
        (flush && typeof flush.resolveMainAiImageByLabel === 'function'
          ? flush.resolveMainAiImageByLabel('#main-flow-container', { builderState: state })
          : state.aiImageByLabel);
    }
  }
  if (!rowAiImageByLabel?.byLabel || !Object.keys(rowAiImageByLabel.byLabel).length) {
    return false;
  }

  const hasSuccessfulCharacter = Object.values(rowAiImageByLabel.byLabel).some((saved) => {
    const urls = (saved?.characterUrls || []).filter((u) => u != null && String(u).trim() !== '');
    if (!urls.length) return false;
    const status = saved?.jobStatus || 'idle';
    const running =
      !!saved?.isGenerating || status === 'queued' || status === 'running';
    return !running && (status === 'succeeded' || !saved?.jobId);
  });
  if (!hasSuccessfulCharacter && !options.force) return false;

  if (!state.bundle.configurations[bid]) state.bundle.configurations[bid] = {};
  const existing = state.bundle.configurations[bid][idx] || {};
  let expanded = existing.properties ? { ...existing.properties } : {};

  if (isAnchor && state.main?.selections) {
    const flatMain =
      typeof pb.flattenAiImageSelectionsForProps === 'function'
        ? pb.flattenAiImageSelectionsForProps(item, state.main.selections)
        : { ...state.main.selections };
    expanded = { ...flatMain, ...expanded };
  }

  if (typeof pb.expandDelimiterProperties === 'function') {
    expanded = pb.expandDelimiterProperties({ ...expanded });
  }

  if (rowAiImageByLabel?.byLabel) {
    Object.entries(rowAiImageByLabel.byLabel).forEach(([label, saved]) => {
      const field =
        (item.config || []).find((f) => f && f.label === label) ||
        { label, type: 'AI Image' };
      mergeAiImageFieldIntoSelections(expanded, saved, field);
    });
  }

  const encoded = encodeBundleRowProperties(item, expanded, pb, {
    rowAiImageByLabel,
    rowAiTextByLabel: isAnchor
      ? resolveMainAiTextByLabel('#main-flow-container', { builderState: { ...state, mode: 'MAIN' } }) || { byLabel: {} }
      : buildAiTextDraftFromCartProps(existing.properties, item.config || [], expanded),
    syncApi: options.syncApi || getSyncApi(pb),
    collectFromDom: false,
  });

  let variantId = existing.id;
  if (!variantId && typeof pb.resolveBundleItemDefaultVariantId === 'function') {
    variantId = pb.resolveBundleItemDefaultVariantId(item);
  }
  if (!variantId && item.variants?.[0]) variantId = item.variants[0].id;

  state.bundle.configurations[bid][idx] = {
    ...existing,
    id: variantId || existing.id,
    properties: encoded,
  };

  if (isAnchor) {
    const syncApi =
      options.syncApi || getSyncApi(pb) || (typeof window !== 'undefined' ? window.PBOfferBundleSync : null);
    if (syncApi && typeof syncApi.bridgeAnchorMediaToMainFlow === 'function' && mainProduct) {
      syncApi.bridgeAnchorMediaToMainFlow({
        mainProduct,
        state,
        anchorProps: state.bundle.configurations[bid][idx].properties,
      });
    }
  }

  return true;
}

/**
 * PB-AI-11 — Resolve bundle id for job terminal when offer not selected yet.
 * Uses selectedId when set; otherwise first bundle with a slot for the active row.
 */
function resolveBundleIdForAiJobTerminal(state, rowIdx, bundles, mainProduct) {
  const selectedId = state?.bundle?.selectedId;
  if (selectedId != null && selectedId !== '') return String(selectedId);

  const configs = state?.bundle?.configurations || {};
  const idx = Number(rowIdx);
  if (Number.isNaN(idx)) return null;

  for (const bundle of bundles || []) {
    if (!bundle?.id) continue;
    const bid = String(bundle.id);
    const rowCfg = configs[bid]?.[idx];
    if (rowCfg) return bid;
  }

  if (mainProduct) {
    for (const bundle of bundles || []) {
      if (!bundle?.id || !bundle.items?.length) continue;
      const bid = String(bundle.id);
      const bundleCfg = configs[bid];
      if (!bundleCfg || !Object.keys(bundleCfg).length) continue;
      const anchorIdx = bundle.items.findIndex(
        (i) => String(i?.id) === String(mainProduct.id)
      );
      if (anchorIdx === idx || anchorIdx >= 0) return bid;
    }
  }

  return null;
}

/**
 * PB-AI-9 — After AI job succeeds, project media into bundle slots + refresh offer UI.
 * @param {{ builderState?: object, pb?: object, jobStatus?: string }} detail
 * @returns {boolean}
 */
export function projectAiMediaAfterJobTerminal(detail) {
  const state = detail?.builderState;
  const pb = detail?.pb || (typeof window !== 'undefined' ? window.productBuilder : null);
  const jobStatus = detail?.jobStatus || 'idle';
  if (!state || !pb || jobStatus !== 'succeeded') return false;

  const data = typeof window !== 'undefined' ? window.BUILDER_DATA : null;
  const bundles = data?.bundles || [];
  const mainProduct = data?.mainProduct;

  let rowIdx = null;
  if (state.mode === 'SUB' && state.sub?.isQueueItem && state.sub.currentId != null) {
    rowIdx = parseInt(state.sub.currentId, 10);
  } else if (mainProduct) {
    for (const bundle of bundles) {
      const anchorIdx = (bundle.items || []).findIndex(
        (i) => String(i?.id) === String(mainProduct.id)
      );
      if (anchorIdx >= 0) {
        rowIdx = anchorIdx;
        break;
      }
    }
  }
  if (rowIdx == null || Number.isNaN(rowIdx)) return false;

  const bid = resolveBundleIdForAiJobTerminal(state, rowIdx, bundles, mainProduct);
  if (!bid) return false;

  const bundle = bundles.find((b) => String(b?.id) === bid);
  if (!bundle) return false;

  const firstMainIdx = bundle.items.findIndex(
    (i) => String(i?.id) === String(mainProduct?.id)
  );

  const projected = projectAiMediaToBundleSlot(state, pb, bid, rowIdx, { bundle, mainProduct });
  if (!projected) return false;

  const slot = state.bundle.configurations[String(bid)]?.[rowIdx];
  const sourceProps = slot?.properties || {};
  const syncApi =
    typeof window !== 'undefined' ? window.PBOfferBundleSync : null;
  if (syncApi && typeof syncApi.applySharedAiMediaFanOut === 'function' && sourceProps) {
    const ownership =
      (typeof window !== 'undefined' && window.PBMediaLineOwnership) || null;
    const allowMediaFanOut =
      ownership && typeof ownership.canSourceFanOutAiMedia === 'function'
        ? ownership.canSourceFanOutAiMedia({
            bundle,
            mainProduct,
            sourceIdx: rowIdx,
          })
        : rowIdx === firstMainIdx;
    syncApi.applySharedAiMediaFanOut({
      bundleId: bid,
      bundle,
      mainProduct,
      state,
      sourceIdx: rowIdx,
      sourceProps,
      allowMediaFanOut,
      expandP13n:
        typeof pb.expandDelimiterProperties === 'function'
          ? (p) => pb.expandDelimiterProperties({ ...p })
          : undefined,
      encodeSyncedRowProperties:
        window.PBBundleRowEncode &&
        typeof window.PBBundleRowEncode.encodeBundleRowProperties === 'function'
          ? function (targetItem, expanded) {
              const aiImageByLabel =
                syncApi.aiImageFromSyncedProps &&
                typeof syncApi.aiImageFromSyncedProps === 'function'
                  ? syncApi.aiImageFromSyncedProps(expanded, targetItem, {
                      getAiImagePhotoKeyFromField: pb.getAiImagePhotoKeyFromField?.bind(pb),
                      getAiImageCharacterKeyFromField: pb.getAiImageCharacterKeyFromField?.bind(pb),
                      isAiImageField: function (field) {
                        return !!(
                          field &&
                          field.type &&
                          String(field.type).toLowerCase().indexOf('ai image') !== -1
                        );
                      },
                    })
                  : undefined;
              return window.PBBundleRowEncode.encodeBundleRowProperties(targetItem, expanded, pb, {
                rowAiImageByLabel: aiImageByLabel,
                trustedAiTextProps: true,
                syncApi,
              });
            }
          : undefined,
      buildSlotProperties: function (props, bundleItem) {
        const opts = bundleItem && bundleItem.options ? bundleItem.options : [];
        return typeof pb.buildItemProperties === 'function'
          ? pb.buildItemProperties(props, opts)
          : props;
      },
      resolveDefaultVariantId: function (item) {
        return typeof pb.resolveBundleItemDefaultVariantId === 'function'
          ? pb.resolveBundleItemDefaultVariantId(item)
          : item?.variants?.[0]?.id;
      },
      isTargetUserEdited: function (_bid, idx, st) {
        const rowSlot = st.bundle.configurations[String(bid)]?.[idx];
        return !!(rowSlot && rowSlot.userEdited);
      },
    });
  }
  if (slot && typeof pb.inlineSyncBundleRowsAfterSave === 'function') {
    pb.inlineSyncBundleRowsAfterSave(String(bid), bundle, rowIdx, slot.properties, slot.id);
  }

  pb.invalidateOffersListContentHash?.();
  pb.renderOffers?.({ intro: 'skip' });
  pb.refreshAiReviewChrome?.();
  pb.checkOfferValidity?.();
  return true;
}

function attachToWindow() {
  if (typeof window === 'undefined') return;
  window.PBBundleRowEncode = {
    mainOwnsDesignLibraryPick,
    resolveRowCatalogDesignLibrary,
    resolveRowAiImageByLabel,
    buildBundleRowEncodeStateSlice,
    encodeBundleRowProperties,
    prepareBundleRowSubContext,
    persistRowAiImageDraftToState,
    normalizeRowAiImageSelectionsForEncode,
    encodeManualBundleRowSave,
    applyMainAiTextProps,
    encodeBundleCartRowProperties,
    resolveRowAiImageForDraft,
    resolveRowCatalogDesignLibraryForDraft,
    resolveCatalogDesignLibraryForCartRow,
    reinforceBundleRowTier1Keys,
    extractExpandedPropsFromSubFlow,
    projectAiMediaToBundleSlot,
    projectAiMediaAfterJobTerminal,
  };
}

attachToWindow();
